class Carousel extends HTMLElement {
  constructor() {
    super();
    this.autoSlide = (this.hasAttribute('auto-slide') && this.getAttribute('auto-slide') !== 'false');
    this.slideSpeed = parseInt(this.getAttribute('slide-speed')) ?? 10;
    this.itemsHolder = this.querySelector('carousel-block-items');
    if(!this.itemsHolder) {
      console.error('Carousel requires a carousel-block-items element.');
    }
  }

  nextItem() {
    this.itemsHolder.nextItem();
  }

  prevItem() {
    this.itemsHolder.prevItem();
  }

  reload() {
    this.itemsHolder.reload();
  }

  hideNav() {
    const prev = this.querySelector('carousel-prev-arrow');
    const next = this.querySelector('carousel-next-arrow');
    if (prev) prev.classList.add('hidden');
    if (next) next.classList.add('hidden');
  }

  showNav() {
    const prev = this.querySelector('carousel-prev-arrow');
    const next = this.querySelector('carousel-next-arrow');
    if (prev) prev.classList.remove('hidden');
    if (next) next.classList.remove('hidden');
  }
}

class CarouselItems extends HTMLElement {
  constructor() {
    super();
    this.setDefaultStyle();
    this.minHeight = 0;
    this.columnGap = 0;
    this.columnWidth = 0;
    this.smallBreakpoint = 1024;
    this.desktopView = true;
    this.carousel = this.closest('carousel-block');
    if(!this.carousel) {
      console.error('No parent carousel-block element found.');
      return;
    }
    this.reload();
    this.handleShiftFirstItem = this.shiftFirstItem.bind(this);
    this.handleShiftLastItem = this.shiftLastItem.bind(this);
    window.addEventListener('resize', () => {
      this.setColumnDimensions();
    });
  }

  getItems() {
    this.items = this.querySelectorAll('carousel-block-item');
  }

  reload() {
    this.getItems();
    this.setColumnDimensions();
  }

  setDefaultStyle() {
    this.style.display = 'flex';
    this.style.flexWrap = 'nowrap';
    this.style.overflow = 'hidden';
    this.style.position = 'relative'
  }

  setColumnDimensions() {
    this.clearAuto();
    this.toggleNav();
    const style = getComputedStyle(this);
    this.columnGap = parseFloat(style.columnGap);
    const width = parseFloat(style.width) + this.columnGap;
    this.desktopView = width > this.smallBreakpoint;
    this.columnWidth = this.desktopView 
      ? (width / this.desktopColumns) - this.columnGap
      : (width / this.mobileColumns) - this.columnGap;
    const heights = [];
    this.items.forEach((i,k) => {
      i.style.minWidth = `${this.columnWidth}px`;
      i.style.width = `${this.columnWidth}px`;
      heights.push(this.calcHeight(i));
      i.style.translate = `${k * (this.columnWidth + this.columnGap)}px`;
    });
    const extraHeight = parseFloat(style.paddingBottom) + parseFloat(style.paddingTop) + parseFloat(style.marginTop) + parseFloat(style.marginBottom);
    this.minHeight = Math.max(...heights) + extraHeight;
    this.style.minHeight = `${this.minHeight}px`
    this.resetAuto();
  }

  calcHeight(element) {
    const clone = element.cloneNode(true);
    Object.assign(clone.style, {
      overflow: 'visible',
      height: 'auto',
      maxHeight: 'none',
      opacity: '0',
      visibility: 'hidden',
      display: 'block'
    });
    this.after(clone);
    const height = clone.offsetHeight;
    clone.remove();
    return height;
  }

  isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  get desktopColumns() {
    return parseInt(this.getAttribute('desktop-columns') ?? 4);
  }

  set desktopColumns(columns) {
    this.setAttribute('desktop-columns', columns);
  }

  get mobileColumns() {
    return parseInt(this.getAttribute('mobile-columns') ?? 2);
  }

  set mobileColumns(columns) {
    this.setAttribute('mobile-columns', columns);
  }

  get numColumns() {
    return this.desktopView ? this.desktopColumns : this.mobileColumns;
  }

  setAuto() {
    if (!Shopify.designMode && this.carousel.autoSlide && this.numColumns < this.items.length) {
      this.auto = setTimeout(() => {
        this.nextItem()
      }, this.carousel.slideSpeed * 1000);
    }
  }

  resetAuto() {
    if(!this.isInViewport(this)) {
      const observer = new IntersectionObserver(function(entries) {
        if(entries[0].isIntersecting === true) {
          entries[0]['target'].clearAuto();
          entries[0]['target'].setAuto();
        }
      }, { threshold: [0]});
      observer.observe(this);
    }
    else {
      if (!Shopify.designMode && this.carousel.autoSlide) {
        this.clearAuto();
        this.setAuto();
      }
    }
  }

  clearAuto() {
    if (!Shopify.designMode && this.carousel.autoSlide) {
      clearTimeout(this.auto);
    }
  }

  toggleNav() {
    this.items.length <= this.numColumns ? this.carousel.hideNav() : this.carousel.showNav();
  }

  shiftFirstItem() {
    const items = [...this.items];
    const firstItem = items[0];
    items.shift();
    items.push(firstItem);
    firstItem.disableTransition();
    firstItem.style.translate = `${(items.length - 1) * (this.columnWidth + this.columnGap)}px`;
    firstItem.enableTransition();
    this.items = items;
  }

  shiftLastItem() {
    const items = [...this.items];
    const lastItem = this.items[this.items.length - 1];
    items.pop();
    items.unshift(lastItem);
    lastItem.disableTransition();
    lastItem.style.translate = `${0 - (this.columnWidth + this.columnGap)}px`;
    lastItem.enableTransition();
    this.items = items;
  }

  nextItem() {
    if(this.numColumns >= this.items.length) {
      return false;
    }
    if(!this.isInViewport(this.items[0])) {
      this.shiftFirstItem();
    }
    const items = [...this.items]
    items.forEach((i,k) => {
      const currPos = parseFloat(i.style.translate)
      i.style.translate = `${currPos - this.columnWidth - this.columnGap}px`;
    })
    this.resetAuto();
  }

  prevItem() {
    if(this.numColumns >= this.items.length) {
      return false;
    }
    if(!this.isInViewport(this.items[this.items.length - 1])) {
      this.shiftLastItem();
    }
    const items = [...this.items]
    items.forEach((i,k) => {
      const currPos = parseFloat(i.style.translate)
      i.style.translate = `${currPos + (this.columnWidth + this.columnGap)}px`;
    })
    this.resetAuto();
  }

  jumpTo(item) {
    const before = [];
    const after = [];
    let found = false;
    this.items.forEach((i,k) => {
      i.disableTransition();
      if(i === item) {
        found = true;
      }
      found ? before.push(i) : after.push(i);
    })
    this.items = [...before, ...after];
    this.setColumnDimensions();
    this.enableTransitions();
  }

  disableTransitions() {
    this.items.forEach(i => {
      i.disableTransition();
    })
  }

  enableTransitions() {
    this.items.forEach(i => {
      i.enableTransition();
    })
  }
}

class CarouselItem extends HTMLElement {
  constructor() {
    super();
    this.screenX = 0;
    this.setDefaultStyle();
    this.setupEvents();
    this.holder = this.closest('carousel-block-items');
  }

  setDefaultStyle() {
    this.style.position = 'absolute';
    this.enableTransition();
  }

  disableTransition () {
    this.style.transition = 'none';
  }

  enableTransition () {
    this.flushCss();
    this.style.transition = 'translate 500ms';
  }

  flushCss() {
    this.offsetHeight;
  }

  setActive() {
    this.holder.jumpTo(this);
  }

  setupEvents() {
    this.addEventListener('touchstart', this.setPageX.bind(this))
    this.addEventListener('touchend', this.checkDragged.bind(this))
  }

  setPageX(e) {
    const evt = (typeof e.originalEvent === 'undefined') ? e : e.originalEvent;
    const touch = evt.touches[0] || evt.changedTouches[0];
    this.screenX = touch.screenX;
  }

  checkDragged(e) {
    const evt = (typeof e.originalEvent === 'undefined') ? e : e.originalEvent;
    const touch = evt.touches[0] || evt.changedTouches[0];
    if(touch.screenX < this.screenX) {
      if((this.screenX - touch.screenX) > 40) {
        this.holder.nextItem();
      }
    }
    else if(touch.screenX > this.screenX) {
      if((touch.screenX - this.screenX) > 40) {
        this.holder.prevItem();
      }
    }
  }
  
}

class CarouselNav extends HTMLElement {
  constructor() {
    super();
    this.carousel = this.closest('carousel-block');
  }

  prevItem() {
    this.carousel.prevItem();
  }

  nextItem() {
    this.carousel.nextItem();
  }
}

class CarouselPrev extends CarouselNav {
  constructor() {
    super();
    this.addEventListener('click', this.prevItem.bind(this));
  }
}

class CarouselNext extends CarouselNav {
  constructor() {
    super();
    this.addEventListener('click', this.nextItem.bind(this));
  }
}

customElements.define('carousel-block', Carousel);
customElements.define('carousel-block-items', CarouselItems);
customElements.define('carousel-block-item', CarouselItem);
customElements.define('carousel-prev-arrow', CarouselPrev);
customElements.define('carousel-next-arrow', CarouselNext);